"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { useStudio } from "@/contexts/studio-context";

/**
 * Ce que les mains font des touches, en deux faits qui vont ensemble.
 *
 * Une porte du rythme demande les deux, et aucun des deux ne suffit :
 *
 * - **Le compte** dit qu'une touche a été *refrappée*. `together` seul
 *   laisserait la porte suivante s'ouvrir à l'instant où elle apparaît, puisque
 *   la main tient déjà l'accord depuis la porte d'avant, et le rythme
 *   défilerait tout seul. Une basse qui rejoue sa fondamentale sur le troisième
 *   temps la tient depuis le premier : seul un compte voit sa frappe.
 *
 * - **L'ensemble** dit que les touches sonnent *en même temps*. Le compte seul
 *   validait un accord joué note après note, aussi lentement qu'on voulait, ce
 *   qui n'est pas un accord.
 *
 * Les deux sortent de la même souscription, et c'est voulu : deux abonnements
 * au piano finiraient par se répondre d'une image d'écart, et la porte se
 * jouerait sur cet écart-là.
 */
export type KeyStrikes = {
  /** Combien de fois chaque touche a été enfoncée depuis le montage. */
  readonly presses: ReadonlyMap<number, number>;
  /** Les touches qui sonnent ensemble : enfoncées, ou frappées à l'instant. */
  readonly together: ReadonlySet<number>;
};

/**
 * Combien de temps une touche reste dans l'ensemble après avoir été enfoncée.
 *
 * ## Ce qu'il tolère, et ce qu'il ne tolère pas
 *
 * Il ne tolère pas de jouer l'accord note après note : à deux dixièmes et demi,
 * un arpège que l'oreille entend comme un arpège est déjà sorti de la fenêtre.
 * Il tolère l'accord roulé, la main qui pose son pouce un souffle avant son
 * auriculaire, et l'image ou deux que l'écran met à voir une frappe. C'est la
 * différence entre « ensemble » et « à la milliseconde », et c'est celle qu'un
 * pianiste fait.
 *
 * ## Le temps se lit à l'événement, jamais au rendu
 *
 * Chaque touche sort de l'ensemble par sa propre minuterie, armée au
 * relâchement pour ce qu'il reste de son délai. L'ensemble est donc toujours
 * juste sans qu'on ait à interroger l'horloge pendant un rendu, ce qui n'est
 * pas pur et ce que le produit ne fait nulle part.
 */
export const STRIKE_GRACE_MS = 250;

export function useKeyStrikes(): KeyStrikes {
  const { studio } = useStudio();
  const [presses, setPresses] = useState<ReadonlyMap<number, number>>(() => new Map());
  const [together, setTogether] = useState<ReadonlySet<number>>(() => new Set());

  /** Les minuteries de sortie en cours, par touche. */
  const timers = useRef(new Map<number, number>());
  /** Quand chaque touche a été enfoncée pour la dernière fois. */
  const pressedAt = useRef(new Map<number, number>());

  useEffect(() => {
    if (!studio) {
      return;
    }

    const clocks = timers.current;
    const stamps = pressedAt.current;

    const drop = (midi: number) => {
      clocks.delete(midi);
      stamps.delete(midi);
      setTogether((prev) => {
        if (!prev.has(midi)) {
          return prev;
        }

        const next = new Set(prev);

        next.delete(midi);

        return next;
      });
    };

    const off = studio.getPiano().on((event) => {
      if (event.type !== "press" && event.type !== "release") {
        return;
      }

      const midi = event.note.toMidi();

      if (event.type === "press") {
        // Une touche réenfoncée repart pour un délai plein, et sa minuterie de
        // sortie est désarmée : elle est là, elle n'a pas à s'expirer.
        const pending = clocks.get(midi);

        if (pending !== undefined) {
          window.clearTimeout(pending);
          clocks.delete(midi);
        }

        stamps.set(midi, performance.now());
        setPresses((prev) => new Map(prev).set(midi, (prev.get(midi) ?? 0) + 1));
        setTogether((prev) => (prev.has(midi) ? prev : new Set(prev).add(midi)));

        return;
      }

      // Au relâchement, elle tient encore ce qu'il reste de son délai, compté
      // depuis la frappe et non depuis le relâchement : c'est la frappe que le
      // délai couvre.
      const rest = STRIKE_GRACE_MS - (performance.now() - (stamps.get(midi) ?? 0));

      if (rest <= 0) {
        drop(midi);

        return;
      }

      clocks.set(midi, window.setTimeout(() => drop(midi), rest));
    });

    return () => {
      off();
      for (const timer of clocks.values()) {
        window.clearTimeout(timer);
      }

      clocks.clear();
      stamps.clear();
    };
  }, [studio]);

  return useMemo(() => ({ presses, together }), [presses, together]);
}
